import { DateTime, parseDateTime, parseTime, toFullTimeFormat, toShortTimeFormat } from "@bounce/date"
import { i18next } from "@bounce/i18n"
import { theme } from "@bounce/theme"
import { isNumber } from "@bounce/util"

import type {
  OpeningHours,
  OpeningHoursException,
  OpeningInfo,
  StoreHoliday,
  StoreOpeningHourExceptions,
  StoreOpeningHours,
} from "../types"
import { DisplayStatus, OpeningStatus } from "../types"

type TimeFormat = "short" | "full"

export type DateInfo = {
  date: DateTime
  openingHours: OpeningHours[]
}

type StoreOpeningHourFragment = {
  openingHours: StoreOpeningHours | null
  openingHourExceptions: StoreOpeningHourExceptions | null
}

type Store = {
  city: {
    timezone: string
  }
  openingHourExceptions: StoreOpeningHourExceptions
  openingHours: StoreOpeningHours
}

// Return a text for describe the opening status: Closed, Open now, Fully booked
const getStatusText = (displayStatus: DisplayStatus, isStoreOpen: boolean) => {
  if (displayStatus === DisplayStatus.OutOfCapacity)
    return i18next.t("helpers.openingHours.fullyBooked", { defaultValue: "Fully booked", ns: "store" })

  if (isStoreOpen) {
    return i18next.t("helpers.openingHours.openNow", { defaultValue: "Open now", ns: "store" })
  }

  return i18next.t("helpers.openingHours.closed", { defaultValue: "Closed", ns: "store" })
}

// Return an extra text to describe the opening status: Opens at, Closes at, Open 24 hours
export const getStatusExtra = (
  currentDate: DateTime,
  status: OpeningStatus,
  openingHours: OpeningHours[],
  openingHourExceptions: OpeningHoursException[],
  isStoreOpen?: boolean,
) => {
  if (isStoreOpen) {
    const todayOpeningHours = getDaysOpeningHours(currentDate, openingHours, openingHourExceptions)
    const is24 = is24Hour(todayOpeningHours)
    if (status === OpeningStatus.Open24 || is24) {
      return i18next.t("helpers.openingHours.open24Hours", { defaultValue: "Open 24 hours", ns: "store" })
    }

    // store is open so we need to find the next closing time
    return getClosesAtText(currentDate, openingHours, openingHourExceptions)
  }
  // store is closed so we need to find the next opening time
  const nextOpenDate = getFirstOpeningHourAfter(currentDate, openingHours, openingHourExceptions)?.hour ?? null
  const isSameDay = currentDate.weekday - 1 === nextOpenDate?.dayOfWeek

  return getOpensAtText(nextOpenDate, isSameDay)
}

export type GetNextStatusTimeOptions = {
  currentDate: DateTime
  openingHours: OpeningHours[]
  openingHourExceptions: OpeningHoursException[]
  isStoreOpen?: boolean
  locale?: string
}

export const getNextStatusTime = ({
  currentDate,
  openingHours,
  openingHourExceptions,
  isStoreOpen,
  locale,
}: GetNextStatusTimeOptions) => {
  if (!isStoreOpen) {
    return getOpensAtTime({ currentDate, openingHours, openingHourExceptions, locale, timeFormat: "full" })
  }

  const todayOpeningHours = getDaysOpeningHours(currentDate, openingHours, openingHourExceptions)
  const is24 = is24Hour(todayOpeningHours)
  if (is24) {
    return "24/7"
  }

  // store is open so we need to find the next closing time
  return getClosesAtTime({ currentDate, openingHours, openingHourExceptions, locale, timeFormat: "full" })
}

const statusColors: Record<OpeningStatus, string> = {
  [OpeningStatus.IsOpen]: theme.color("green-700"),
  [OpeningStatus.IsOpenNow]: theme.color("green-700"),
  [OpeningStatus.Open24]: theme.color("green-700"),
  [OpeningStatus.IsClosedAtCheckIn]: theme.color("red-700"),
  [OpeningStatus.IsClosedAtCheckOut]: theme.color("red-700"),
  [OpeningStatus.IsClosed]: theme.color("red-700"),
  [OpeningStatus.OutOfCapacity]: theme.color("red-700"),
  [OpeningStatus.MaxDaysExceeded]: theme.color("red-700"),
}

// Parse and extends the storage spot opening hours info
export const getOpeningInfo = (
  store: { displayStatus: DisplayStatus; city: { timezone: string } } & StoreOpeningHourFragment,
  checkIn?: DateTime,
  checkOut?: DateTime,
  isStoreOpen?: boolean,
): OpeningInfo => {
  const openingHours = parseOpeningHours(store.openingHours)
  const openingHourExceptions = parseOpeningHoursExceptions(store.openingHourExceptions)
  const checkInDate = checkIn || DateTime.now()
  const checkOutDate = checkOut || DateTime.now()
  const currentDate = DateTime.fromObject({}, { zone: store.city.timezone })

  const checkInInfo = {
    date: checkInDate,
    openingHours: getDaysOpeningHours(checkInDate, openingHours, openingHourExceptions),
  }

  const checkOutInfo = {
    date: checkOutDate,
    openingHours: getDaysOpeningHours(checkOutDate, openingHours, openingHourExceptions),
  }

  const status = getOpeningStatus({
    displayStatus: store.displayStatus,
    checkIn: checkInInfo,
    checkOut: checkOutInfo,
    hasSelectedDates: !!checkIn && !!checkOut,
  })
  const color = isStoreOpen ? statusColors[OpeningStatus.IsOpenNow] : statusColors[OpeningStatus.IsClosed]
  if (!color) throw new Error("Invalid OpeningInfo color")
  const text = getStatusText(store.displayStatus, !!isStoreOpen)
  const extra = getStatusExtra(currentDate, status, openingHours, openingHourExceptions, !!isStoreOpen)

  return {
    status,
    text,
    extra,
    color,
    openingHours,
    openingHourExceptions,
    checkIn: checkInInfo,
    checkOut: checkOutInfo,
  }
}

export type GetClosesAtTimeOptions = {
  currentDate: DateTime
  openingHours: OpeningHours[]
  openingHourExceptions: OpeningHoursException[]
  locale?: string
  timeFormat?: TimeFormat
}

export const getClosesAtTime = ({
  currentDate,
  openingHours,
  openingHourExceptions,
  locale,
  timeFormat = "short",
}: GetClosesAtTimeOptions): string | null => {
  const nextCloseDate = getFirstClosingHourAfter(currentDate, openingHours, openingHourExceptions)?.hour ?? null

  // We can do this because we only use the time, not the day here
  const closeDate = parseTime(nextCloseDate?.closeTime ?? "", DateTime.now())
  if (!closeDate) {
    return null
  }

  return timeFormat === "full" ? toFullTimeFormat(closeDate, locale) : toShortTimeFormat(closeDate, locale)
}

const getClosesAtText = (
  currentDate: DateTime,
  openingHours: OpeningHours[],
  openingHourExceptions: OpeningHoursException[],
): string | null => {
  const time = getClosesAtTime({ currentDate, openingHours, openingHourExceptions })
  return i18next.t("store:helpers.openingHours.closesAtV2", "Closes at {time}", { time })
}

/*
  Returns if the given day of week corresponds to tomorrow or not.
  This function assumes days of week from 1 (Monday) to 7 (Sunday)
*/
const isTomorrow = (dayOfWeek: number) => {
  const tomorrowDayOfWeek = (DateTime.local().weekday % 7) + 1
  return dayOfWeek === tomorrowDayOfWeek
}

export type GetOpensAtTimeOptions = {
  currentDate: DateTime
  openingHours: OpeningHours[]
  openingHourExceptions: OpeningHoursException[]
  locale?: string
  timeFormat?: TimeFormat
}

export const getOpensAtTime = ({
  currentDate,
  openingHours,
  openingHourExceptions,
  locale,
  timeFormat = "short",
}: GetOpensAtTimeOptions): string | null => {
  const nextOpenDate = getFirstOpeningHourAfter(currentDate, openingHours, openingHourExceptions)?.hour ?? null

  const openDate = parseTime(nextOpenDate?.openTime ?? "", DateTime.now())
  if (!openDate) {
    return null
  }

  return timeFormat === "full" ? toFullTimeFormat(openDate, locale) : toShortTimeFormat(openDate, locale)
}

const getOpensAtText = (openingHour: OpeningHours | null, isSameDay: boolean): string | null => {
  // We can do this because we only use the time, not the day here
  const openDate = parseTime(openingHour?.openTime ?? "", DateTime.now())
  if (!openDate) {
    return null
  }

  const dayOfWeek = openingHour?.dayOfWeek
  if (!isSameDay && isNumber(dayOfWeek)) {
    // Need to add 1 because days of week on opening hours are from 0 to 6
    return isTomorrow(dayOfWeek + 1)
      ? i18next.t("store:helpers.openingHours.opensTomorrow", "Opens tomorrow")
      : i18next.t("store:helpers.openingHours.opensV2", "Opens {time}", {
          time: DateTime.local()
            .set({ weekday: dayOfWeek + 1 })
            .toLocaleStringGlobal({ weekday: "long" }),
        })
  }

  return i18next.t("store:helpers.openingHours.opensAtV2", "Opens at {time}", { time: toShortTimeFormat(openDate) })
}

export const getOpeningHours = (day: DateTime, openingHours: OpeningHours[] = []): OpeningHours[] => {
  const dayOfWeek = day.weekday - 1
  const result = openingHours
    .filter(o => o.dayOfWeek === dayOfWeek)
    .sort((oh1, oh2) => {
      if (oh1.openTime && oh2.openTime) {
        return oh1.openTime > oh2.openTime ? 1 : -1
      }
      return 0
    })

  return result.length > 0 ? result : [{ closed: true }]
}

export const getDaysOpeningHours = (
  day: DateTime,
  openingHours: OpeningHours[] = [],
  openingHourExceptions: OpeningHoursException[] = [],
  holidays: StoreHoliday[] = [],
): OpeningHours[] => {
  let result: OpeningHours[] = []

  const dayExceptions = getOpeningHourExceptionsOn(day, openingHourExceptions)

  const firstException = dayExceptions[0]

  const dayOfWeek = day.weekday - 1
  // If there's an opening hour exception, we want to
  // return either null or a modified opening hour
  if (firstException) {
    const openingHourExceptionHoliday = firstException.holidayId
      ? holidays.find(h => h.id === firstException.holidayId)
      : undefined
    // Whenever we find a holiday that matches the holiday ID of the exception,
    // it means that it is confirmed
    const confirmedHoliday = openingHourExceptionHoliday
      ? { ...openingHourExceptionHoliday, isConfirmed: true }
      : undefined
    // Store is closed on exception date
    if (firstException.closed) {
      result = [{ closed: true, holiday: confirmedHoliday }]
    } else {
      // Store has availability on exception date
      result = dayExceptions.map(ohe => {
        return {
          openTime: ohe.openTime ?? "",
          closeTime: ohe.closeTime ?? "",
          dayOfWeek,
          closed: false,
          holiday: confirmedHoliday,
        }
      })
    }
  } else {
    result = openingHours
      .filter(o => o.dayOfWeek === dayOfWeek)
      .sort((oh1, oh2) => {
        if (oh1.openTime && oh2.openTime) {
          return oh1.openTime > oh2.openTime ? 1 : -1
        }
        return 0
      })
  }
  result = result.length > 0 ? result : [{ closed: true }]

  // If the result doesn't have a holiday already through the
  // exception, we get all unconfirmed holidays here and pass them in
  let holiday: StoreHoliday | undefined
  const noHolidayInResult = !result[0]?.holiday

  if (noHolidayInResult) {
    holiday = getHolidayOn(day, holidays)
  }

  if (holiday && noHolidayInResult) {
    result = result.map(oh => {
      return { ...oh, holiday: { ...holiday, isConfirmed: false } }
    })
  }

  return result
}

export const getOpeningHourExceptionsOn = (
  date: DateTime,
  openingHourExceptions: OpeningHoursException[] = [],
): OpeningHoursException[] => {
  return openingHourExceptions.filter(op => op.startDate.startOf("day") <= date && op.endDate.endOf("day") >= date)
}

export const getOpeningHoursOn = ({
  date,
  openingHours,
  openingHoursExceptions,
  holidays = [],
}: {
  date: DateTime
  openingHours: OpeningHours[]
  openingHoursExceptions: OpeningHoursException[]
  holidays?: StoreHoliday[]
}): OpeningHours[] => {
  const dayHours = getDaysOpeningHours(date, openingHours, openingHoursExceptions, holidays)
  const previousDayHours = getDaysOpeningHours(date.minus({ days: 1 }), openingHours, openingHoursExceptions)
  return mergeDayOpeningHours(previousDayHours, dayHours)
}

export const isOpenOn = ({
  date,
  openingHours,
  openingHoursExceptions,
}: {
  date: DateTime
  openingHours: OpeningHours[]
  openingHoursExceptions: OpeningHoursException[]
}) => {
  return getOpeningHoursOn({ date, openingHours, openingHoursExceptions }).find(o => o.closed === true) === undefined
}

// Check if the storage spot is close at a date time
export const isWithinHours = (date: DateTime, openingHours: OpeningHours) => {
  if (!openingHours.openTime || !openingHours.closeTime || openingHours.closed) {
    return false
  }

  // If the store is open 24 hours we will always return true
  if (is24Hour([openingHours])) {
    return true
  }

  const { openTime, closeTime } = openingHours

  const openDate = parseTime(openTime, date)
  let closeDate = parseTime(closeTime, date)

  if (!closeDate || !openDate) {
    throw new Error("Could not parse openTime or closeTime")
  }

  closeDate = openDate > closeDate ? closeDate.plus({ days: 1 }) : closeDate

  return date >= openDate && date <= closeDate
}

// Check if the storage spot is open at a date time
export const isOpenAtTime = (
  date: DateTime | undefined,
  openingHours: OpeningHours[],
  openingHourExceptions: OpeningHoursException[],
) => {
  if (!date) return true
  const dayHours = getDaysOpeningHours(date, openingHours, openingHourExceptions)
  const previousDayHours = getDaysOpeningHours(date.minus({ days: 1 }), openingHours, openingHourExceptions)
  const hours = mergeDayOpeningHours(previousDayHours, dayHours)
  return hours.some(openingHour => isWithinHours(date, openingHour))
}

// Check if the storage spot is open 24h/24h
export const is24Hour = (openingHours: OpeningHours[]) => {
  const firstHour = openingHours[0]

  return !!firstHour && firstHour.openTime === firstHour.closeTime
}

// Check if the storage spot is open after
export const willOpenLater = (date: DateTime, openingHours: OpeningHours[]) => {
  return openingHours.some(oh => {
    if (oh.openTime) {
      const openTime = parseTime(oh.openTime, date)
      if (openTime === undefined) {
        throw new Error(`Could not parse openTime ${oh.openTime}`)
      }

      return openTime > date
    }
    return false
  })
}

// Parse opening Hours Exceptions
export const parseOpeningHoursExceptions = (ohxs?: StoreOpeningHourExceptions): OpeningHoursException[] => {
  const openingHoursExceptions: OpeningHoursException[] = (ohxs ?? []).map(ohx => {
    const parsedStartDate = parseDateTime(ohx?.startDate)
    const parsedEndDate = parseDateTime(ohx?.endDate)

    if (!parsedStartDate || !parsedEndDate) {
      throw new Error("Opening hours exception malformed")
    }

    return {
      id: ohx?.id ?? undefined,
      openTime: ohx?.openTime ?? undefined,
      closeTime: ohx?.closeTime ?? undefined,
      closed: ohx?.closed ?? true,
      startDate: parsedStartDate,
      endDate: parsedEndDate,
      reason: ohx?.reason ?? "",
      holidayId: ohx?.holidayId ?? undefined,
    }
  })
  return openingHoursExceptions
}

// Parse opening Hours
export const parseOpeningHours = (ohs?: StoreOpeningHours): OpeningHours[] => {
  return (
    ohs?.map(e => ({
      openTime: e?.openTime ?? "",
      closeTime: e?.closeTime ?? "",
      dayOfWeek: e?.dayOfWeek ?? -1,
      closed: false,
    })) ?? []
  )
}

export const isClosed = (openingHours: OpeningHours[]) => {
  return !!openingHours.filter(oh => oh.closed === true).length
}

// Extend the storage spot display status with WillOpen and WillClose
export const getOpeningStatus = ({
  displayStatus,
  checkIn,
  checkOut,
  hasSelectedDates,
}: {
  displayStatus: DisplayStatus
  checkIn: DateInfo
  checkOut: DateInfo
  hasSelectedDates: boolean
}) => {
  if (!hasSelectedDates && displayStatus === DisplayStatus.Open) {
    const now = DateTime.local()
    const isCheckInOpen = isOpenAtTime(now, checkIn.openingHours, [])
    return isCheckInOpen ? OpeningStatus.IsOpenNow : OpeningStatus.IsClosed
  }

  switch (displayStatus) {
    case DisplayStatus.Open: {
      const is24 = is24Hour(checkIn.openingHours) && is24Hour(checkOut.openingHours)
      if (is24) return OpeningStatus.Open24
      return OpeningStatus.IsOpen
    }

    default:
      return displayStatusToOpeningStatus[displayStatus]
  }
}

export const displayStatusToOpeningStatus: Record<DisplayStatus, OpeningStatus> = {
  [DisplayStatus.Closed]: OpeningStatus.IsClosed,
  [DisplayStatus.OutOfCapacity]: OpeningStatus.OutOfCapacity,
  [DisplayStatus.MaxDaysExceeded]: OpeningStatus.MaxDaysExceeded,
  [DisplayStatus.Open]: OpeningStatus.IsOpen,
  [DisplayStatus.ClosedAtCheckIn]: OpeningStatus.IsClosedAtCheckIn,
  [DisplayStatus.ClosedAtCheckOut]: OpeningStatus.IsClosedAtCheckOut,
}

// returns the window for that date, the next one for checkIn and the last for checkOut
export const relevantOpeningHourWindow = (
  date: DateTime,
  checkIn: boolean,
  openingHours: OpeningHours[],
  openingHourExceptions: OpeningHoursException[],
  holidays: StoreHoliday[] = [],
) => {
  const dateHours = getDaysOpeningHours(date, openingHours, openingHourExceptions, holidays)
  return (
    dateHours
      .sort((oh1, oh2) => {
        if (oh1.openTime && oh2.openTime) {
          const sort = checkIn ? 1 : -1
          return oh1.openTime > oh2.openTime ? sort : -sort
        }
        return 0
      })
      .find(oh => {
        if (oh.openTime && oh.closeTime) {
          const openTime = parseTime(oh.openTime, date)
          const closeTime = parseTime(oh.closeTime, date)

          if (!openTime || !closeTime) {
            return false
          }

          if (date > openTime && date < closeTime) {
            // we are on this slot
            return true
          }
          // check for the next relevant timeslot, which is the slot we are not a part of
          // and happens after our check-in date for check-in, or
          // before our check-out date for checkout
          // (this works because we sorted the array backwards in the check-out case)

          return (checkIn && date < openTime) || (!checkIn && date > closeTime)
        }
        // If we couldn't find an opening hour during the specified day, we will try to find one during the next week
      }) ??
    getFirstOpeningHourAfter(date, openingHours, openingHourExceptions)?.hour ??
    null
  )
}

// Gets the next opening hour, starting from the provided date
export const getFirstOpeningHourAfter = (
  date: DateTime,
  openingHours: OpeningHours[],
  openingHourExceptions: OpeningHoursException[],
): { date: DateTime; hour: OpeningHours } | null => {
  if (openingHours.length === 0) {
    return null
  }
  // try to find an opening hour on the same day, but later
  const hoursOnDay = getOpeningHoursOn({
    date,
    openingHours,
    openingHoursExceptions: openingHourExceptions,
    holidays: [],
  })
  const laterHour = hoursOnDay.find(oh => {
    const parsedTime = oh.openTime && parseTime(oh.openTime, date)
    if (oh.openTime && parsedTime && parsedTime > date) {
      return oh
    }
  })

  if (laterHour) {
    return { date, hour: laterHour }
  }

  // try to find an opening hour on the next days
  // or next day if it closes after midnight
  for (let dayDelta = 0; dayDelta < 6; dayDelta++) {
    const day = date.plus({ days: dayDelta + 1 })
    const hoursOnDay = getDaysOpeningHours(day, openingHours, openingHourExceptions)
    const firstHour = hoursOnDay[0]
    if (firstHour && !firstHour.closed) {
      return { date: day, hour: firstHour }
    }
  }

  return null
}

// Gets the next closing hour, starting from the provided date
const getFirstClosingHourAfter = (
  date: DateTime,
  openingHours: OpeningHours[],
  openingHourExceptions: OpeningHoursException[],
): { date: DateTime; hour: OpeningHours } | null => {
  if (openingHours.length === 0) {
    return null
  }

  // try to find a closing hour on the same day, but later
  const hoursOnDay = getOpeningHoursOn({
    date,
    openingHours,
    openingHoursExceptions: openingHourExceptions,
    holidays: [],
  })
  const laterHour = hoursOnDay.find(oh => {
    if (oh.closeTime) {
      const parsedTime = parseTime(oh.closeTime, date)

      if (oh.openTime && oh.openTime > oh.closeTime) {
        return parsedTime
      }

      return parsedTime && parsedTime > date
    }
  })

  if (laterHour) {
    return { date, hour: laterHour }
  }

  return null
}

// Our weekday calculation differs from the one from Luxon:
// Bounce: Mon: 0, Sun: 6
// Luxon: Mon: 1, Sun: 7
// This is why we need to make both systems compatible with each other here
export const getWeekdayByDate = (date: DateTime) => {
  return date.weekday - 1
}

// Returns the current day's opening hours and adds the previous day's opening hours that end after midnight
const mergeDayOpeningHours = (previousDay: OpeningHours[], currentDay: OpeningHours[]): OpeningHours[] => {
  if (previousDay.length === 0) {
    return currentDay
  }

  // If the last entry from yesterday has a closeTime before the openTime,
  // it means the current day ends after midnight
  const lastEntry = previousDay[previousDay.length - 1]

  if (
    lastEntry &&
    !lastEntry.closed &&
    typeof lastEntry.dayOfWeek === "number" &&
    !!lastEntry.closeTime &&
    !!lastEntry.openTime &&
    lastEntry.closeTime < lastEntry.openTime
  ) {
    return [
      {
        openTime: "00:00:00",
        closeTime: lastEntry.closeTime,
        closed: false,
        dayOfWeek: lastEntry.dayOfWeek === 6 ? 0 : lastEntry.dayOfWeek + 1,
        holiday: currentDay[0]?.holiday,
      },
      ...currentDay,
    ]
  }

  return currentDay
}

const getHolidayOn = (date: DateTime, holidays: StoreHoliday[] = []) =>
  holidays.find(h => {
    const datetime = parseDateTime(h.date)
    if (!datetime) return false

    return datetime.startOf("day") <= date && datetime.endOf("day") >= date
  })

export const removeClosedForTheDayOpeningHourExceptions = (openingHourExceptions: StoreOpeningHourExceptions) => {
  if (openingHourExceptions === null) return []

  return openingHourExceptions.filter(oh => oh?.reason !== "Close for the day")
}

export const isOpenNow = (store: Store): boolean => {
  const now = DateTime.fromObject({}, { zone: store.city.timezone })
  const openingHours = parseOpeningHours(store.openingHours)
  const openingHourExceptions = parseOpeningHoursExceptions(store.openingHourExceptions)

  return (
    isOpenOn({ date: now, openingHours, openingHoursExceptions: openingHourExceptions }) &&
    isOpenAtTime(now, openingHours, openingHourExceptions)
  )
}

export const willStoreOpenLater = (store: Store): boolean => {
  const now = DateTime.fromObject({}, { zone: store.city.timezone })
  const openingHours = parseOpeningHours(store.openingHours)
  const openingHourExceptions = parseOpeningHoursExceptions(store.openingHourExceptions)

  return willOpenLater(now, getDaysOpeningHours(now, openingHours, openingHourExceptions))
}
